import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const ComparisonSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });

  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30,
      scale: 0.98,
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const comparisonData = [
    {
      title: "기존 카드현금화",
      isRecommended: false,
      color: "bg-gray-100",
      border: "border-gray-200",
      features: [
        { text: "카드 한도내 이용 가능", positive: true },
        { text: "서비스 이용 가능", positive: true },
        { text: "할부 불가능/ 익월 일시불상환", positive: false },
        { text: "이용시 신용점수 하락", positive: false },
        { text: "신용카드 현금화보다 비싼 이자수수료", positive: false },
      ],
    },
    {
      title: "이노페이",
      isRecommended: true,
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      border: "border-blue-200",
      features: [
        { text: "카드 한도내 이용가능", positive: true },
        { text: "할부 12개월 (무이자지원)", positive: true },
        { text: "신용점수에 영향X", positive: true },
        { text: "현금서비스보다 저렴한 이자 수수료", positive: true },
      ],
    },
    {
      title: "현금서비스/카드론",
      isRecommended: false,
      color: "bg-red-50",
      border: "border-red-200",
      features: [
        { text: "카드론 대출을 신청하신분", positive: false },
        { text: "이런 상황에 이노페이를 이용해보세요!", positive: true },
        { text: "신용등급에 지장없이 현금이 필요하신분", positive: true },
        {
          text: "대출이 꺼리고 주변에 부탁하기 난감한 상황에 현금화가 필요할때",
          positive: true,
        },
      ],
    },
  ];

  const features = [
    {
      title: "신용카드 현금화",
      description:
        "과거에는 신용카드 현금화을 진행하기 위해 일부 오프라인 매장에 방문하여, 가맹점 업주와의 협의를 통해 현금화할 수 있었습니다. 이 방법은 자신이 보유한 신용카드로 가맹점에서 결제한 뒤, 정해진 수수료를 지불하고 현금으로 돌려받는 방법이었습니다.",
      icon: "💳",
      highlight:
        "현재는 온라인에서 신용카드 현금화 진행하는 방법이 다양하게 생기면서 많은 사람들이 이용할 수 있게 되었습니다.",
    },
    {
      title: "안전한 결제, 신속한 입금",
      description:
        "고객의 안전이 최우선시되어야 합니다. 철저한 보안 시스템으로 안전한 결제를 보장합니다. 또한 최신 기술을 도입하여 고객님의 개인정보를 안전하게 보호합니다.",
      icon: "🛡️",
      highlight:
        "고객님의 소중한 시간을 존중하고, 이 모든 것을 지키며, 상담부터 입금까지 걸리는 시간은 단 5분이면 충분합니다.",
    },
    {
      title: "다양한 현금화 서비스",
      description:
        "이노페이는 고객님들의 개인정보를 위해 빠르고 안전한 결제 방식을 지원합니다. 신용카드 현금화, 그리고 카드한도 내 현금화 서비스까지 다양한 현금화 서비스를 제공합니다.",
      icon: "⚡",
      highlight:
        "현금이 필요한 고객님의 현금 수요를 신속하게 해결해 드릴 수 있도록 항상 노력하고 있습니다.",
    },
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          className="text-center mb-16"
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? "animate" : "initial"}
        >
          <motion.div variants={fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              이노페이 vs 다른 서비스 비교
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              기존 서비스들과 비교하여 이노페이의 우수성을 확인해보세요
            </p>
          </motion.div>
        </motion.div>

        {/* Comparison Cards */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, margin: "-100px" }}
        >
          {comparisonData.map((item, index) => (
            <motion.div key={index} variants={fadeInUp} className="relative">
              {item.isRecommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    ⭐ 추천 서비스
                  </span>
                </div>
              )}

              <motion.div
                className={`${item.color} rounded-2xl p-6 border-2 ${
                  item.border
                } shadow-lg hover:shadow-xl transition-all duration-300 ${
                  item.isRecommended ? "ring-2 ring-blue-300" : ""
                }`}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 },
                }}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
                  {item.title}
                </h3>

                <div className="space-y-3">
                  {item.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        feature.positive
                          ? "bg-green-50 border border-green-200"
                          : "bg-red-50 border border-red-200"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          feature.positive ? "bg-green-500" : "bg-red-500"
                        }`}
                      >
                        {feature.positive ? (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        className={`text-sm ${
                          feature.positive ? "text-green-700" : "text-red-700"
                        } lg:text-base`}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Features */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, margin: "-100px" }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full"
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 },
                }}
              >
                <div className="text-4xl mb-4 text-center">{feature.icon}</div>

                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                  {feature.title}
                </h3>

                <p className="text-gray-600 leading-relaxed mb-4">
                  {feature.description}
                </p>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <p className="text-blue-800 font-medium text-sm leading-relaxed">
                    💡 {feature.highlight}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Why 이노페이 Section */}
        <motion.div
          className="mt-20 bg-white rounded-3xl p-8 md:p-12 shadow-xl"
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, margin: "-100px" }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              이노페이만의 특별한 장점
            </h3>
            <p className="text-xl text-gray-600">
              다른 어떤 서비스보다 우수한 이유
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl">⚡</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">빠른 처리</h4>
              <p className="text-sm text-gray-600">최대 3분 내 빠른 입금</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl">💰</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">최저 수수료</h4>
              <p className="text-sm text-gray-600">업계 최저 수수료 보장</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl">🚫</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">
                신용조회 없음
              </h4>
              <p className="text-sm text-gray-600">신용점수 영향 없음</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl">🌙</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">
                24시간 서비스
              </h4>
              <p className="text-sm text-gray-600">365일 언제든 이용 가능</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;
